import { css } from "styled-components";
import { breakpoints } from "./variables";
import cssLogisticsResets from "./css.logisticsResets";
import sanitize from "./css.sanitize";

/* stylelint-disable no-descending-specificity */

// Note: All our global styles are under: .dhl-cj-main
// we dont put them under the body to prevent CSS clashes with dhl.com CSS

const body = css`
  ${sanitize}
  :root {
    --gaq-color-cool-blue-800: #b2def3ff;

    /* Custom variables */

    /* Positive sizes */
    --gaq-size-space-3px: 3px;
    --gaq-size-space-5px: 5px;
    --gaq-size-space-7px: 7px;
    --gaq-size-space-9px: 9px;
    --gaq-size-space-11px: 11px;
    --gaq-size-space-13px: 13px;
    --gaq-size-space-15px: 15px;
    --gaq-size-space-23px: 23px;
    --gaq-size-space-25px: 25px;
    --gaq-size-space-26px: 26px;
    --gaq-size-space-29px: 29px;
    --gaq-size-space-30px: 30px;
    --gaq-size-space-34px: 34px;
    --gaq-size-space-35px: 35px;
    --gaq-size-space-38px: 38px;
    --gaq-size-space-45px: 45px;
    --gaq-size-space-50px: 50px;
    --gaq-size-space-51px: 51px;
    --gaq-size-space-52px: 52px;
    --gaq-size-space-56px: 56px;
    --gaq-size-space-60px: 60px;
    --gaq-size-space-70px: 70px;
    --gaq-size-space-90px: 90px;
    --gaq-size-space-120px: 120px;
    --gaq-size-space-130px: 130px;
    --gaq-size-space-180px: 180px;

    /* Negative sizes */
    --gaq-size-space--1px: -1px;
    --gaq-size-space--2px: -2px;
    --gaq-size-space--3px: -3px;
    --gaq-size-space--5px: -5px;
    --gaq-size-space--6px: -6px;
    --gaq-size-space--8px: -8px;
    --gaq-size-space--10px: -10px;
    --gaq-size-space--12px: -12px;
    --gaq-size-space--14px: -14px;
    --gaq-size-space--30px: -30px;

    /* custom-font-sizes */
    --gaq-size-font-21px: 21px;
    --gaq-size-font-25px: 25px;
    --gaq-size-font-27px: 27px;
    --gaq-size-font-28px: 28px;
    --gaq-size-font-31px: 31px;
    --gaq-size-font-33px: 33px;
    --gaq-size-font-36px: 36px;
    --gaq-size-font-39px: 39px;
  }

  /*
   Why do we have the difference on how the body and html height are defined?
   This is a good explanation: https://www.kirupa.com/html5/make_body_take_up_full_browser_height.html
   TL;DR: We want to ensure that the height of the body and our app container (dhl-cj-main) expand as big as its content.
   We also want to allow the browser to calculate the diffence between the clientHeight and the scrollHeight, so that
   JS scrolls work.
  */
  html {
    height: 100%;
  }

  body,
  .dhl-cj-main {
    min-height: 100%;
  }

  html,
  body,
  .dhl-cj-main {
    width: 100%;
  }

  .dhl-cj-main {
    /* We need to define ltr here so the plugin will replace to rtl */
    direction: ltr;
    /* We want the background color to apply only to our container and not to the whole dhl */
    background-color: #fbfbfb;
    color: var(--dui-color-black-500);
    display: block;
    font-family: var(--dui-font-family);
    font-size: var(--dui-size-font-sm);
    line-height: var(--dui-size-line-height-sm);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: auto;
    -webkit-text-size-adjust: 100%;

    @media print {
      background-color: var(--dui-color-white-500);
      display: block;
    }

    ${cssLogisticsResets}

    p,
    span,
    button,
    input,
    select,
    textarea {
      font-family: var(--dui-font-family);
    }

    input[type="checkbox"] + div {
      background-color: var(--dui-color-white-500);
    }

    input,
    textarea,
    select {
      font-size: var(--dui-size-font-md);
      line-height: var(--dui-size-line-height-sm);
    }

    input,
    textarea,
    select {
      @media screen and (min-width: ${breakpoints.tablet}) {
        font-size: var(--dui-size-font-md);
      }

      @media screen and (min-width: ${breakpoints.desktop}) {
        font-size: var(--dui-size-font-md);
      }
    }

    input::placeholder {
      color: var(--dui-colors-action-foreground-ghost-muted);
    }

    select::-ms-expand {
      display: none;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    .L1,
    .L2,
    .L3,
    .L4,
    .L5,
    .L6 {
      font-family: var(--dui-font-family);
      color: var(--dui-color-black-500);
      font-stretch: condensed;
    }

    h1,
    .L1 {
      font-size: var(--gaq-size-font-33px);
      line-height: 36px;

      @media screen and (min-width: ${breakpoints.tablet}) {
        font-size: var(--gaq-size-font-36px);
        line-height: 40px;
      }

      @media screen and (min-width: ${breakpoints.desktop}) {
        font-size: var(--gaq-size-font-39px);
        line-height: 43px;
      }
    }

    h2,
    .L2 {
      font-size: var(--gaq-size-font-25px);
      line-height: 29px;

      @media screen and (min-width: ${breakpoints.tablet}) {
        font-size: var(--gaq-size-font-28px);
        line-height: 33px;
      }

      @media screen and (min-width: ${breakpoints.desktop}) {
        font-size: var(--gaq-size-font-31px);
        line-height: 35px;
      }
    }

    h3,
    .L3 {
      font-size: var(--gaq-size-font-21px);
      line-height: 25px;

      @media screen and (min-width: ${breakpoints.tablet}) {
        font-size: var(--dui-size-font-3xl);
        line-height: 29px;
      }

      @media screen and (min-width: ${breakpoints.desktop}) {
        font-size: var(--gaq-size-font-27px);
        line-height: 31px;
      }
    }

    h4,
    .L4 {
      font-size: var(--dui-size-font-xl);
      line-height: 24px;

      @media screen and (min-width: ${breakpoints.tablet}) {
        font-size: var(--dui-size-font-lg);
        line-height: 24px;
      }

      @media screen and (min-width: ${breakpoints.desktop}) {
        font-size: var(--dui-size-font-xl);
        line-height: 24px;
      }
    }

    h5,
    .L5 {
      font-size: var(--dui-size-font-md);
      line-height: 20px;

      @media screen and (min-width: ${breakpoints.tablet}) {
        font-size: var(--dui-size-font-md);
        line-height: 20px;
      }

      @media screen and (min-width: ${breakpoints.desktop}) {
        font-size: var(--dui-size-font-md);
        line-height: 22px;
      }
    }

    h6,
    .L6 {
      font-size: var(--dui-size-font-sm);
      line-height: 18px;

      @media screen and (min-width: ${breakpoints.tablet}) {
        font-size: var(--dui-size-font-sm);
        line-height: 18px;
      }

      @media screen and (min-width: ${breakpoints.desktop}) {
        font-size: var(--dui-size-font-sm);
        line-height: 18px;
      }
    }

    ul,
    ol {
      list-style-position: inherit;
    }
  }

  @media print {
    .c-media-tiles,
    .c-voc-footer--sitemap,
    .c-go-to-top-button {
      display: none;
    }
  }

  svg {
    /* RTL aligment Fix */
    /*! @noflip */
    direction: ltr;
  }

  #__SVG_SPRITE_NODE__ {
    display: none;
  }

  .freezeScroll {
    margin: 0;
    padding: 0;
    overflow: hidden;
  }

  .visually-hidden {
    position: absolute !important;
    clip: rect(1px 1px 1px 1px); /* IE6, IE7 */
    clip: rect(1px, 1px, 1px, 1px);
    padding: 0 !important;
    border: 0 !important;
    height: 1px !important;
    width: 1px !important;
    overflow: hidden;
  }

  .grid-fluid,
  .grid-maxWidth,
  .grid-maxWidthInner {
    margin-right: auto;
    margin-left: auto;

    &:not(.no-padding) {
      padding-left: var(--dui-size-space-10x);
      padding-right: var(--dui-size-space-10x);
    }

    @media screen and (min-width: ${breakpoints.tablet}) {
      &:not(.no-padding) {
        padding-left: unset;
        padding-right: unset;
      }
    }
  }

  .grid-fluid:not(.no-padding) {
    @media screen and (min-width: ${breakpoints.desktop}) {
      padding-left: calc(var(--dui-size-space-80x) / 2);
      padding-right: calc(var(--dui-size-space-80x) / 2);
    }
  }

  .grid-maxWidth {
    max-width: 136.5rem;
  }

  .grid-maxWidthInner {
    max-width: 100rem;

    &:not(.no-padding) {
      margin-left: var(--dui-size-space-10x);
      margin-right: var(--dui-size-space-10x);
      padding-left: 0;
      padding-right: 0;
      width: auto;
    }

    @media screen and (min-width: ${breakpoints.tablet}) {
      &:not(.no-padding) {
        margin-left: calc(var(--dui-size-space-10x) + var(--dui-size-space-5x));
        margin-right: calc(
          var(--dui-size-space-10x) + var(--dui-size-space-5x)
        );
      }
    }

    @media screen and (min-width: ${breakpoints.desktop}) {
      &:not(.no-padding) {
        margin-left: auto;
        margin-right: auto;
      }
    }
  }

  .mx-wrapper {
    margin-left: var(--dui-size-space-10x);
    margin-right: var(--dui-size-space-10x);

    @media screen and (min-width: ${breakpoints.tablet}) {
      margin-left: calc(var(--dui-size-space-10x) + var(--dui-size-space-5x));
      margin-right: calc(var(--dui-size-space-10x) + var(--dui-size-space-5x));
    }
  }
`;

export default body;
